import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { leadService, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  UserIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Spinner } from '../components/Spinner';

export const LeadTransfer = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: '',
    page: 1,
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);

  const { data: leadsData, isLoading: leadsLoading } = useQuery(
    ['leads', filters],
    () => leadService.getLeads(filters),
    {
      keepPreviousData: true,
    }
  );

  const { data: salespersonsData } = useQuery(
    'salespersons',
    () => userService.getUsers({ role: 'sales_executive', status: 'active' }),
    {
      enabled: hasPermission('leads', 'update'),
    }
  );

  const transferLeadMutation = useMutation(leadService.assignLead, {
    onSuccess: () => {
      queryClient.invalidateQueries('leads');
      queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
      setShowTransferModal(false);
      setSelectedLead(null);
      setTransferTo('');
      alert('Lead transferred successfully!');
    },
    onError: (error) => {
      alert(error.response?.data?.message || 'Failed to transfer lead');
    },
  });

  const handleTransfer = async () => {
    if (!selectedLead || !transferTo) {
      alert('Please select a lead and salesperson to transfer');
      return;
    }

    await transferLeadMutation.mutateAsync({
      leadId: selectedLead._id,
      assignedTo: transferTo,
      teamRole: 'sales_executive',
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/leads')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lead Transfer</h1>
            <p className="mt-2 text-gray-600">Transfer leads between salespersons</p>
            <Link to="/leads" className="text-indigo-600 hover:text-indigo-900">
              View All Leads
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-4 sm:px-6 sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label className="sr-only">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  className="input"
                  placeholder="Search leads..."
                />
              </div>
              <div>
                <label className="sr-only">Assigned To</label>
                <select
                  value={filters.assignedTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value, page: 1 }))}
                  className="input"
                >
                  <option value="">All Salespersons</option>
                  {salespersonsData?.data?.users?.map(salesperson => (
                    <option key={salesperson._id} value={salesperson._id}>
                      {salesperson.firstName} {salesperson.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {leadsLoading ? (
          <div className="p-8 text-center">
            <Spinner size="lg" />
            <p className="mt-2 text-gray-500">Loading leads...</p>
          </div>
        ) : leadsData?.data?.leads?.length === 0 ? (
          <div className="p-8 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.assignedTo
                ? 'Try adjusting your filters'
                : 'No leads available for transfer'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLead(leadsData?.data?.leads?.[0]);
                          } else {
                            setSelectedLead(null);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="table-header-cell">Customer</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Priority</th>
                    <th className="table-header-cell">Assigned To</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {leadsData?.data?.leads?.map((lead) => (
                    <tr key={lead._id} className="table-row">
                      <td className="table-cell">
                        <input
                          type="checkbox"
                          checked={selectedLead?._id === lead._id}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLead(lead);
                            } else {
                              setSelectedLead(null);
                            }
                          }}
                          className="rounded"
                        />
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.customerInfo.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {lead.customerInfo.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {lead.customerInfo.phone}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div>
                          {lead.leadInfo.brand} {lead.leadInfo.model}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          lead.status === 'new' ? 'badge-primary' :
                          lead.status === 'follow_up' ? 'badge-warning' :
                          lead.status === 'converted' ? 'badge-success' :
                          'badge-secondary'
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          lead.priority === 'low' ? 'badge-secondary' :
                          lead.priority === 'medium' ? 'badge-warning' :
                          lead.priority === 'high' ? 'badge-primary' :
                          'badge-danger'
                        }`}>
                          {lead.priority}
                        </span>
                      </td>
                      <td className="table-cell">
                        {lead.assignment.assignedTo ? (
                          <div className="text-sm text-gray-900">
                            {lead.assignment.assignedTo?.firstName} {lead.assignment.assignedTo?.lastName}
                          </div>
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-500">
                          {format(new Date(lead.createdAt), 'MM/dd/yyyy')}
                        </div>
                      </td>
                      <td className="table-cell">
                        {hasPermission('leads', 'update') && (
                          <Link to={`/leads/${lead._id}`} className="text-indigo-600 hover:text-indigo-900">
                            <PencilIcon className="h-4 w-4" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Transfer Button */}
            {hasPermission('leads', 'update') && selectedLead && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="btn-primary"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Transfer Selected Lead
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Transfer Lead</h3>
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Transfer <strong>{selectedLead?.customerInfo?.name}</strong> to a new salesperson
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer To
                </label>
                <select
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Select Salesperson</option>
                  {salespersonsData?.data?.users?.map(salesperson => (
                    <option key={salesperson._id} value={salesperson._id}>
                      {salesperson.firstName} {salesperson.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="btn-outline-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  className="btn-primary"
                >
                  Transfer Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
